import React, { useEffect, useState } from 'react';
import SpendCardPlatformButton from './spend-card/spend-card-platform-button';
import MetricChart from 'src/pages/reports/components/metric-chart';
import { ReportMetrics, TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';
import { AggregateSourceData, PlatformData } from '../types/performance.types';
import { Loader2 } from 'lucide-react';


interface SpendAnalyticsCardProps {
  isLoading?: boolean;
  platforms: Record<string, PlatformData>;
  editMode?: boolean;
  mergeMode?: boolean;
  aggregateData: Record<string, ReportMetrics>;
  timeSeriesData: Record<string, AggregateSourceData[]>;
  selectedMetric?: string;
  startDate?: string;
  endDate?: string;
  handleMarginPercent?: (platformName: string, margin: number) => void;
  handlePlatformClick?: (platform: PlatformData) => void;
}


const SpendAnalyticsCard: React.FC<SpendAnalyticsCardProps> = ({
  isLoading = false,
  platforms,
  aggregateData,
  timeSeriesData,
  editMode = false,
  mergeMode = false,
  selectedMetric = 'Clicks',
  startDate = '2025-01-01',
  endDate = '2025-01-31',
  handleMarginPercent,
  handlePlatformClick,
}) => {
  const [activeTimeSeriesData, setActiveTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);


  useEffect(() => {
    const dateMap = new Map<string, any>();

    const allDates = new Set<string>();
    Object.values(timeSeriesData).forEach(platformData => {
      if (Array.isArray(platformData)) {
        platformData.forEach(dataPoint => {
          if (dataPoint.date) {
            allDates.add(dataPoint.date);
          }
        });
      }
    });

    Array.from(allDates).sort().forEach(date => {
      dateMap.set(date, { date });
    });

    if (mergeMode) {
      // In merge mode, combine all enabled platforms into a single "Merged" line
      Object.entries(timeSeriesData).forEach(([platformName, platformData]) => {
        const platform = platforms[platformName];
        if (Array.isArray(platformData) && platform?.enabled) {
          platformData.forEach(dataPoint => {
            if (dataPoint.date && dateMap.has(dataPoint.date)) {
              const dateEntry = dateMap.get(dataPoint.date);
              let metricValue = dataPoint[selectedMetric.toLowerCase() as keyof typeof dataPoint] || 0;

              // Apply margin for spend metric
              if (selectedMetric.toLowerCase() === 'spend' && platform.margin) {
                metricValue = Number(metricValue) * (1 + platform.margin / 100);
              }

              // Add to merged total (initialize if not exists)
              if (!dateEntry['Merged']) {
                dateEntry['Merged'] = 0;
              }
              dateEntry['Merged'] += Number(metricValue);
            }
          });
        }
      });
    } else {
      // In normal mode, show individual platform data
      Object.entries(timeSeriesData).forEach(([platformName, platformData]) => {
        if (Array.isArray(platformData)) {
          platformData.forEach(dataPoint => {
            if (dataPoint.date && dateMap.has(dataPoint.date)) {
              const dateEntry = dateMap.get(dataPoint.date);
              // Use the selected metric as the key for this platform
              const metricValue = dataPoint[selectedMetric.toLowerCase() as keyof typeof dataPoint] || 0;
              dateEntry[platformName] = metricValue;
            }
          });
        }
      });
    }

    const _timeSeriesData = Array.from(dateMap.values()).sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    setActiveTimeSeriesData(_timeSeriesData);
  }, [timeSeriesData, selectedMetric, platforms, mergeMode]);

  // Handle platform toggle
  const handlePlatformToggle = (platform: PlatformData) => {
    handlePlatformClick?.(platform);
  };

  return (
    <div className={`relative bg-white rounded-lg shadow-md overflow-hidden ${isLoading ? 'animate-pulse opacity-50' : ''}`}>
      {/* Platform Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-0">
        {Object.values(platforms).map((platform, index) => {
          return (
            <SpendCardPlatformButton
              selectedMetric={selectedMetric}
              editMode={editMode}
              key={platform.name}
              color={platform.color}
              data={platform}
              onChange={handlePlatformToggle}
            />
          );
        })}
      </div>
      {isLoading && Object.keys(platforms).length === 0 && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-green/50">
          <Loader2 className="w-16 h-16 animate-spin text-blue-500 opacity-100 rounded-full" />
        </div>
      )}

      <MetricChart
        className='border-none'
        data={activeTimeSeriesData}
        metrics={mergeMode
          ? [{
            key: 'Merged',
            name: 'Total ' + selectedMetric,
            color: '#000000', // Use a default blue color for merged line
          }]
          : Object.values(platforms)
            .filter(platform => platform.enabled) // Only show enabled platforms
            .map(platform => ({
              key: platform.name,
              name: platform.displayName,
              color: platform.chartColor,
            }))
        }
        isLoading={isLoading}
        // sharedYAxisDomain={true}
      />
    </div>
  );
};

export default SpendAnalyticsCard;
